import type { File } from "schemas/generated/file";
import type {
  FileTemplate,
  GeneratedFileTemplate,
} from "schemas/generated/fileTemplate";

import React, { useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import previewPage from "@/features/funnel/assets/preview-page.png";
import PageRenderer from "@/features/react-editor/components/PageRenderer";
import { useViewportStore } from "@/features/react-editor/store/viewportStore";
import Button from "@replo/design-system/components/button/Button";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import CreatePageMenu from "./components/CreatePageMenu";
import ExistingPageMenu from "./components/ExistingPageMenu";

export type Page = Pick<
  File,
  "id" | "name" | "pageRoute" | "createdAt" | "updatedAt"
>;

export type TemplateItem = FileTemplate | GeneratedFileTemplate;

function SelectedPagePreview({
  menuType,
  pageRoute,
}: {
  menuType: string | null;
  pageRoute?: string | null;
}) {
  const currentViewport = useViewportStore((state) => state.currentViewport);

  if (!pageRoute) {
    return (
      <div className="h-full w-full border-0.5 border-border rounded-lg flex items-center justify-center bg-neutral-soft">
        <div className="text-center flex flex-col items-center gap-2">
          <img src={previewPage} alt="preview page" className="w-[150px]" />
          <div className="text-sm font-medium">
            {menuType === "new"
              ? "Select a template to preview"
              : "Select a page to preview"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full border-0.5 border-border rounded-lg overflow-auto bg-neutral-soft">
      <div
        className="flex h-full w-full justify-center min-h-full"
        style={{
          width: "100%",
        }}
      >
        <div
          className="flex-shrink-0"
          style={{
            width:
              currentViewport !== "desktop"
                ? `${CANVAS_DATA[currentViewport].defaultFrameWidth}px`
                : "100%",
          }}
        >
          <PageRenderer pageRoute={pageRoute} showControls={false} />
        </div>
      </div>
    </div>
  );
}

function CreateNewPage() {
  const projectId = useCurrentProjectId();
  const [selectedPageTemplate, setSelectedPageTemplate] =
    useState<TemplateItem | null>(null);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);

  const { funnelId } = useParams<{ funnelId: string }>();
  const [searchParams] = useSearchParams();
  const menuType = searchParams.get("menu-type");
  const navigate = useNavigate();

  const { mutate: addFunnelStep, isPending: isAddingFunnelStep } =
    trpc.funnel.addFunnelStep.useMutation({
      onSuccess: () => {
        void trpcUtils.funnel.find.invalidate();
        void trpcUtils.funnel.list.invalidate({ projectId });
        navigate(`/shops/${projectId}/funnels/${funnelId}`);
      },
    });

  const handleNewPageSuccess = ({ createdFile }: { createdFile: File }) => {
    void trpcUtils.webContainer.getFileTree.invalidate({
      projectId,
    });
    void trpcUtils.nextJSProject.listPages.invalidate({ projectId });

    if (funnelId && projectId) {
      addFunnelStep({
        projectId,
        funnelId: funnelId,
        fileId: createdFile.id,
      });
    } else {
      navigate(`/shops/${projectId}/site/${createdFile.id}`);
    }
  };

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    trpc.nextJSProject.createPageFromTemplate.useMutation({
      onSuccess: handleNewPageSuccess,
    });

  const {
    mutate: createPageFromContent,
    isPending: isCreatingPageFromContent,
  } = trpc.nextJSProject.createOrUpdatePageComponent.useMutation({
    onSuccess: handleNewPageSuccess,
  });

  const handleCreatePage = () => {
    if (menuType === "new") {
      if (!projectId || !selectedPageTemplate) {
        return;
      }

      // handle generated file template
      if ("content" in selectedPageTemplate) {
        void createPageFromContent({
          projectId,
          pageRoute: `/${selectedPageTemplate.name
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^\da-z-]/g, "")}`,
          content: selectedPageTemplate.content,
          name: selectedPageTemplate.name,
        });
      } else {
        // handle file template
        void createPageFromTemplate({
          projectId,
          templateId: selectedPageTemplate.id,
        });
      }
    } else {
      if (!projectId || !selectedPage || !funnelId) {
        return;
      }
      void addFunnelStep({
        projectId,
        funnelId: funnelId,
        fileId: selectedPage.id,
      });
    }
  };

  const getPreviewPageRoute = () => {
    if (menuType === "new") {
      return selectedPageTemplate
        ? `/template-preview/${selectedPageTemplate?.id}`
        : null;
    }

    return selectedPage?.pageRoute;
  };

  const isAddingPageToFunnel =
    isCreatingPageFromContent || isAddingFunnelStep || isCreatingPage;

  return (
    <>
      <div className="flex h-full">
        <div className="w-1/3 h-full overflow-y-auto no-scrollbar border-t-0.5 border-border py-3">
          {menuType === "new" ? (
            <CreatePageMenu
              selectedPageTemplate={selectedPageTemplate}
              setSelectedPageTemplate={setSelectedPageTemplate}
            />
          ) : (
            <ExistingPageMenu
              funnelId={funnelId}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
          )}
        </div>
        <div className="w-2/3 ml-3 pb-3 h-full">
          <SelectedPagePreview
            pageRoute={getPreviewPageRoute()}
            menuType={menuType as "new" | "existing"}
          />
        </div>
      </div>

      {(selectedPageTemplate || selectedPage) && (
        <div className="absolute bottom-0 left-0 w-full h-16 bg-white flex gap-2 justify-end items-center px-6 fade-in border-t-0.5 border-border z-[2147480003]">
          <Button
            variant="secondary"
            size="base"
            onClick={() => {
              if (menuType === "new") {
                setSelectedPageTemplate(null);
              } else {
                setSelectedPage(null);
              }
            }}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            size="base"
            onClick={handleCreatePage}
            isLoading={isAddingPageToFunnel}
            disabled={isAddingPageToFunnel}
          >
            Select Page
          </Button>
        </div>
      )}
    </>
  );
}

export default CreateNewPage;
